import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  Link2,
  MapPin,
  Pencil,
  XCircle,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/Dialog";

function formatCoord(val: any): string | null {
  const n = Number(val);
  return Number.isFinite(n) ? n.toFixed(5) : null;
}

function distanceLabel(miles: number | null): string | null {
  if (miles === null) return null;
  if (miles < 0.1) return "< 0.1 miles away";
  return `${miles.toFixed(1)} miles away`;
}

function MatchLevelPill({ level }: { level: "strong" | "possible" }) {
  return level === "strong" ? (
    <span className="inline-flex items-center rounded-full bg-warning-weak px-2 py-0.5 text-xs font-medium text-warning">
      Strong possible match
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
      Possible match
    </span>
  );
}

export default function AdminLakeReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin_lake_review_detail", id],
    queryFn: () =>
      fetch(`/app-api/admin/lake-reviews/${encodeURIComponent(id!)}`).then(
        (r) => {
          if (!r.ok) throw new Error("Forbidden or not found");
          return r.json();
        },
      ),
  });

  // Dialog state
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [working, setWorking] = useState(false);
  const [resultError, setResultError] = useState<string | null>(null);

  async function doApprove() {
    setWorking(true);
    setResultError(null);
    try {
      const res = await fetch(
        `/app-api/admin/lake-reviews/${encodeURIComponent(id!)}/approve`,
        { method: "POST" },
      );
      const approveResult = await res.json();
      if (!res.ok) throw new Error(approveResult.error || "Could not approve");
      queryClient.invalidateQueries({ queryKey: ["admin_lake_reviews"] });
      setApproveOpen(false);
      navigate("/admin/lake-reviews", {
        state: {
          success: `${approveResult.canonical_name} approved and listed.`,
        },
      });
    } catch (err) {
      setResultError(err instanceof Error ? err.message : "Could not approve");
    } finally {
      setWorking(false);
    }
  }

  async function doLink(targetId: string) {
    setWorking(true);
    setResultError(null);
    try {
      const res = await fetch(
        `/app-api/admin/lake-reviews/${encodeURIComponent(id!)}/link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target_canonical_id: targetId }),
        },
      );
      const linkResult = await res.json();
      if (!res.ok) throw new Error(linkResult.error || "Could not link");
      queryClient.invalidateQueries({ queryKey: ["admin_lake_reviews"] });
      setLinkTarget(null);
      navigate("/admin/lake-reviews", {
        state: { success: `Submission linked to ${linkResult.linked_to}.` },
      });
    } catch (err) {
      setResultError(err instanceof Error ? err.message : "Could not link");
    } finally {
      setWorking(false);
    }
  }

  async function doReject() {
    setWorking(true);
    setResultError(null);
    try {
      const res = await fetch(
        `/app-api/admin/lake-reviews/${encodeURIComponent(id!)}/reject`,
        { method: "POST" },
      );
      const rejectResult = await res.json();
      if (!res.ok) throw new Error(rejectResult.error || "Could not reject");
      queryClient.invalidateQueries({ queryKey: ["admin_lake_reviews"] });
      setRejectOpen(false);
      navigate("/admin/lake-reviews", {
        state: { success: `${rejectResult.canonical_name} rejected.` },
      });
    } catch (err) {
      setResultError(err instanceof Error ? err.message : "Could not reject");
    } finally {
      setWorking(false);
    }
  }

  if (isLoading)
    return (
      <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
        Loading submission…
      </div>
    );
  if (error || !data)
    return (
      <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-error">
        Submission not found or access denied.{" "}
        <Link to="/admin/lake-reviews" className="text-accent hover:underline">
          Back to reviews
        </Link>
      </div>
    );

  const { submission, matches } = data;
  const hasStrongMatch = matches.some((m: any) => m.match_level === "strong");

  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/admin/lake-reviews"
          className="text-sm font-medium text-accent hover:underline"
        >
          Community Lake Reviews
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-primary">
          {submission.canonical_name}
        </h1>
        <p className="mt-1 text-sm text-secondary">
          Community submission — pending review
        </p>
      </header>

      {/* Submission details */}
      <section className="rounded-lg border border-border bg-raised p-4 space-y-3">
        <h2 className="text-base font-semibold text-primary">
          Submission details
        </h2>
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
          <Detail label="Lake name" value={submission.canonical_name} />
          <Detail label="Venue / fishery" value={submission.venue_name} />
          <Detail label="Postcode" value={submission.postcode} />
          <Detail label="County / region" value={submission.county_region} />
          <Detail label="Country" value={submission.country} />
          <Detail label="Water type" value={submission.water_type} />
          <Detail
            label="Coordinates"
            value={
              formatCoord(submission.latitude) &&
              formatCoord(submission.longitude)
                ? `${formatCoord(submission.latitude)}, ${formatCoord(submission.longitude)}`
                : null
            }
          />
          <Detail label="Submitted by" value={submission.submitted_by_name} />
          <Detail label="Email" value={submission.submitted_by_email} />
        </div>
        {submission.source_notes ? (
          <p className="text-xs text-secondary italic border-t border-border pt-3">
            {submission.source_notes}
          </p>
        ) : null}
      </section>

      {/* Duplicate detection */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-primary">
          Directory duplicate check
        </h2>

        {hasStrongMatch && (
          <div className="flex items-start gap-3 rounded-md border border-warning bg-warning-weak p-3 text-sm text-warning">
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            <p>
              Possible directory duplicate found — review the matches before
              approving this as a new lake.
            </p>
          </div>
        )}

        {matches.length === 0 ? (
          <div className="rounded-lg border border-border bg-raised p-4 text-sm text-secondary">
            No strong directory matches found.
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((match: any) => {
              const reasons: string[] = [];
              if (match.name_similarity >= 0.6)
                reasons.push("Very similar lake name");
              else if (match.name_similarity >= 0.35)
                reasons.push("Similar lake name");
              if (match.venue_similarity >= 0.5)
                reasons.push("Similar venue / fishery");
              if (match.postcode_score >= 0.7)
                reasons.push("Same postcode area");
              else if (match.postcode_score >= 0.3)
                reasons.push("Same county area");
              if (match.distance_miles !== null && match.distance_miles < 5)
                reasons.push(distanceLabel(match.distance_miles)!);

              return (
                <div
                  key={match.id}
                  className="rounded-lg border border-border bg-raised p-4 space-y-3"
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <MatchLevelPill level={match.match_level} />
                      <p className="mt-2 font-semibold text-primary">
                        {match.canonical_name}
                      </p>
                      {match.venue_name ? (
                        <p className="text-sm text-secondary">
                          {match.venue_name}
                        </p>
                      ) : null}
                      <p className="flex items-center gap-1 mt-1 text-sm text-secondary">
                        <MapPin className="size-3.5 shrink-0" />
                        {[match.postcode, match.county_region, match.country]
                          .filter(Boolean)
                          .join(", ") || "Location not recorded"}
                      </p>
                    </div>
                  </div>
                  {reasons.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary mb-1">
                        Reasons
                      </p>
                      <ul className="space-y-0.5">
                        {reasons.map((r) => (
                          <li
                            key={r}
                            className="flex items-center gap-2 text-sm text-secondary"
                          >
                            <span className="size-1 shrink-0 rounded-full bg-secondary" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setLinkTarget({
                        id: match.id,
                        name: match.canonical_name,
                      })
                    }
                  >
                    <Link2 className="size-4" />
                    Link to this existing lake
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Actions */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-primary">Actions</h2>
        {resultError && (
          <p
            className="rounded-md bg-error-weak p-3 text-sm text-error"
            role="alert"
          >
            {resultError}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setApproveOpen(true)}>
            <CheckCircle className="size-4" />
            Approve as new lake
          </Button>
          <Button variant="secondary" onClick={() => setRejectOpen(true)}>
            <XCircle className="size-4" />
            Reject submission
          </Button>
        </div>
        <div className="border-t border-border pt-3">
          <Button variant="secondary" asChild>
            <Link to={`/admin/canonical-lakes/${encodeURIComponent(id!)}/edit`}>
              <Pencil className="size-4" />
              Edit lake details
            </Link>
          </Button>
          <p className="mt-1.5 text-xs text-secondary">
            Correct name, venue, location or other directory fields without
            changing the moderation decision.
          </p>
        </div>
      </section>

      {/* Approve confirmation dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve as new lake?</DialogTitle>
            <DialogDescription>
              <strong>{submission.canonical_name}</strong> will be listed in the
              public lake directory. The submitter's personal lake record
              remains linked and intact.
            </DialogDescription>
          </DialogHeader>
          {resultError && <p className="text-sm text-error">{resultError}</p>}
          <DialogFooter>
            <Button
              variant="tertiary"
              onClick={() => setApproveOpen(false)}
              disabled={working}
            >
              Cancel
            </Button>
            <Button isLoading={working} onClick={doApprove}>
              Approve and publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject confirmation dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject submission?</DialogTitle>
            <DialogDescription>
              <strong>{submission.canonical_name}</strong> will be removed from
              the review queue and kept off the public directory. The
              submitter's personal My Lakes record is not deleted — they can
              still use it privately.
            </DialogDescription>
          </DialogHeader>
          {resultError && <p className="text-sm text-error">{resultError}</p>}
          <DialogFooter>
            <Button
              variant="tertiary"
              onClick={() => setRejectOpen(false)}
              disabled={working}
            >
              Cancel
            </Button>
            <Button variant="secondary" isLoading={working} onClick={doReject}>
              Reject submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link confirmation dialog */}
      <Dialog
        open={linkTarget !== null}
        onOpenChange={(open) => {
          if (!open) setLinkTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to existing lake?</DialogTitle>
            <DialogDescription>
              The submission for <strong>{submission.canonical_name}</strong>{" "}
              will be marked as a duplicate of{" "}
              <strong>{linkTarget?.name}</strong>. The submitter's personal My
              Lakes entry will be re-linked to the existing lake. Sessions and
              catches are not affected.
            </DialogDescription>
          </DialogHeader>
          {resultError && <p className="text-sm text-error">{resultError}</p>}
          <DialogFooter>
            <Button
              variant="tertiary"
              onClick={() => setLinkTarget(null)}
              disabled={working}
            >
              Cancel
            </Button>
            <Button
              isLoading={working}
              onClick={() => linkTarget && doLink(linkTarget.id)}
            >
              Confirm link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-0.5 truncate font-medium text-primary">{value || "—"}</p>
    </div>
  );
}
